<div class="flex flex-col pr-2 pl-2 md:pl-6">
  <h2 class="title-large text-primary mb-4 md:mb-8" data-testid="details-title">{{ 'Personal details' | translate }}</h2>

  <form [formGroup]="settingsForm" class="flex flex-col" data-testid="details-form">
    <!-- Name -->
    <div class="flex flex-col md:flex-row items-start mb-1">
      <div class="w-full md:w-1/3">
        <div class="w-4/5 label-xlarge text-on-surface-variant mb-4">{{ 'Name' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="flex flex-col md:flex-row md:gap-6 w-full lg:w-4/5">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>{{ 'First name' | translate }}</mat-label>
            <input matInput type="text" formControlName="firstName" data-testid="first-name-input" />
            <mat-error *ngIf="settingsForm.get('firstName')?.hasError('required')">{{ 'First name is required' | translate }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>{{ 'Last name' | translate }}</mat-label>
            <input matInput type="text" formControlName="lastName" data-testid="last-name-input" />
            <mat-error *ngIf="settingsForm.get('lastName')?.hasError('required')">{{ 'Last name is required' | translate }}</mat-error>
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Email address -->
    <div class="flex flex-col md:flex-row items-start">
      <div class="w-full md:w-1/3">
        <div class="w-4/5 label-xlarge text-on-surface-variant mb-4">{{ 'Email address' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>{{ 'Email address' | translate }}</mat-label>
            <input matInput type="email" formControlName="email" readonly data-testid="email-input" />
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="border-t border-surface-container mt-1 mb-6"></div>

    <!-- Your photo -->
    <div class="flex flex-col md:flex-row items-start">
      <div class="w-full md:w-1/3">
        <div class="w-4/5 mb-4">
          <div class="label-xlarge text-on-surface-variant">{{ 'Your photo' | translate }}</div>
          <div class="body-medium text-outline">{{ 'This will be displayed on your profile' | translate }}</div>
        </div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5 flex flex-col md:flex-row items-center md:items-start gap-5">
          <div class="relative w-14 h-14 rounded-full overflow-hidden shrink-0 flex items-center justify-center">
            @if (displayAvatarUrl) {
            <img [src]="displayAvatarUrl" class="w-full h-full object-cover rounded-full" alt="Profile" />
            } @else {
            <mat-icon svgIcon="user-circle" class="icon-size [--icon-size:100%] [--icon-stroke-width:0.7px] !text-outline-variant"></mat-icon>
            }
          </div>
          <label class="relative w-full" data-testid="photo-upload-label">
            <input type="file" accept="image/*" (change)="onFilesChanged($event)" class="hidden" data-testid="photo-upload-input" />
            <div class="bg-surface-bright border border-surface-varient rounded px-6 py-4 flex flex-col items-center gap-3 cursor-pointer"
                 [class.border-primary]="isDragging"
                 [class.border-2]="isDragging"
                 (dragover)="onDragOver($event)"
                 (dragenter)="onDragEnter($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onDrop($event)">
              <div class="w-12 h-12 rounded-full bg-primary-container flex items-center justify-center">
                <span class="label-large text-on-primary-container">↑</span>
              </div>
              <div class="text-center">
                <p class="label-large text-outline"><span class="label-large-prominent text-primary">{{ 'Click to upload' | translate }}</span> {{ 'or drag and drop' | translate }}</p>
                <p class="label-medium text-outline mt-1">{{ 'PNG, JPG or JPEG (max. 800x800px)' | translate }}</p>
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>

    <div class="border-t border-surface-container my-6"></div>

    <!-- Speciality (hidden for Legal organisations) -->
    <div *ngIf="!isLegalOrg" class="flex flex-col md:flex-row items-start">
      <div class="w-full md:w-1/3">
        <div class="w-4/5 label-xlarge text-on-surface-variant mb-4">{{ 'Speciality' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5">
        <mat-form-field appearance="outline" class="w-full">
          <mat-select formControlName="specialty" [placeholder]="'Select a speciality' | translate" data-testid="specialty-select">
            <mat-option class="search-option" disabled>
              <div class="search-container">
                <div class="relative flex items-center">
                  <mat-icon class="absolute left-3 text-outline pointer-events-none">search</mat-icon>
                  <input
                    type="text"
                    class="w-full pl-12 pr-10 py-2 border border-outline rounded body-large text-on-surface focus:outline-none focus:border-primary"
                    [placeholder]="'Search' | translate"
                    [value]="searchTerm"
                    (input)="searchSpecialty($event)"
                    (click)="$event.stopPropagation()"
                    (keydown)="onSearchKeyDown($event)"
                  />
                  <button
                    *ngIf="searchTerm"
                    type="button"
                    class="absolute right-0 top-1/2 -translate-y-1/2 text-outline hover:text-on-surface cursor-pointer flex items-center justify-center"
                    (click)="clearSearch($event)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </mat-option>
            <mat-option [value]="''">{{ 'Select a speciality' | translate }}</mat-option>
            <mat-option *ngFor="let specialty of filteredSpecialties" [value]="specialty">{{ specialty }}</mat-option>
          </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Actions -->
     <div class="flex items-start mt-7 sticky bottom-0 z-10 py-4 bg-surface-container-lowest">
      <div class="hidden md:block md:w-1/3"></div>
      <div class="w-full md:w-2/3 flex">
        <div class="w-full lg:w-4/5 flex items-end gap-4 justify-end">
          <button class="flex-1 [--mdc-outlined-button-container-height:36px] md:flex-none md:[--mdc-outlined-button-container-height:48px]" 
            mat-stroked-button color="primary" type="button" (click)="cancel()" [disabled]="isLoadingDetails" data-testid="details-cancel-button">
            {{ 'Cancel' | translate }}</button>
          <button class="flex-1 [--mdc-filled-button-container-height:36px] md:flex-none md:[--mdc-filled-button-container-height:48px]" 
            mat-flat-button color="primary" type="button" (click)="saveChanges()" [disabled]="!settingsForm.valid || !settingsForm.dirty || isLoadingDetails" data-testid="details-save-button">
            <app-loading-spinner [loading]="isLoadingDetails" [diameter]="20">
              {{ 'Save changes' | translate }}
            </app-loading-spinner>
          </button>
        </div>
      </div>
     </div>
    
  </form>
</div> 